import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { TwoLineCell } from 'table/components/TwoLineCell';
import { Trans, t } from '@lingui/macro';
import { useSearchParams } from 'react-router-dom';
import { useNotification } from 'features/notifications';
import { CircularProgress } from '@mui/material';
import { useOrgId } from 'features/accessControl';
import { Modal } from 'features/modalManager';
import { Id } from 'react-toastify';
import { useViewEvent, useEvent } from 'lib/segment';
import { useUser } from 'features/auth';
import { useGetMergeRequest } from '../api/useGetMergeRequst';
import { usePostMergeRequest } from '../api/usePostMergeRequest';
import { onApproveReject } from '../utils/events';
import { Text } from 'components/text/Text';
import { Checkbox } from 'components/inputs/Checkbox';
import { Select } from 'components/inputs/Select';

export const AccountMerge = () => {
  const { notify, dismissNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const mergeRequestId = searchParams.get('mergeRequestId');
  const { data: userData } = useUser();
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string>>({});
  const [selectedOrganizations, setSelectedOrganizations] = useState<Record<string, string[]>>({});
  const [skipUsers, setSkipUsers] = useState<Record<string, boolean>>({});
  const [dismissNotifyId, setDismissNotifyId] = useState<Id | null>(null);
  const event = useEvent();

  const supplierId = useOrgId()?.toString() || '';

  const { data: mergeRequest, isLoading, error } = useGetMergeRequest(mergeRequestId, supplierId);

  useViewEvent(
    'Merge Request Details Viewed',
    {
      objectType: 'Page',
      targetType: 'Merge Request',
      targetId: mergeRequestId,
      businessContext: {
        status: mergeRequest?.data?.status,
        legacyUsersCount: mergeRequest?.data?.legacyUsers.length,
        designerOrganizationId: mergeRequest?.data?.supplierPortalOrganization.id,
      },
    },
    Boolean(mergeRequest?.data)
  );

  const mergeRequestActionMutation = usePostMergeRequest(
    supplierId,
    mergeRequestId || '',
    approved => {
      if (dismissNotifyId) {
        dismissNotification(dismissNotifyId);
        setDismissNotifyId(null);
      }
      onApproveReject(
        approved,
        mergeRequestId,
        mergeRequest,
        event,
        userData,
        selectedOrganizations
      );
    }
  );

  useEffect(() => {
    if (mergeRequest?.data && !mergeRequest.data.notFound) {
      const initialRoles: Record<string, string> = {};
      const initialOrganizations: Record<string, string[]> = {};
      const initialSkipUsers: Record<string, boolean> = {};

      mergeRequest.data.legacyUsers.forEach(user => {
        if (user.showDropdowns) {
          if (user.selectedRole) {
            initialRoles[user.legacyUserId] = user.selectedRole.id.toString();
          }
          if (user.selectedDesignerOrganizations?.length) {
            initialOrganizations[user.legacyUserId] = user.selectedDesignerOrganizations.map(org =>
              org.id.toString()
            );
          }
        }
        // initialize skip checkbox as checked by default for users with role ID 3
        if (user.showSkip) {
          initialSkipUsers[user.legacyUserId] = true;
        }
      });

      setSelectedRoles(initialRoles);
      setSelectedOrganizations(initialOrganizations);
      setSkipUsers(initialSkipUsers);
    }

    if (mergeRequest?.data && mergeRequest.data.notFound) {
      notify.info({
        id: 'merge_request_not_found',
        message: 'merge_request_not_found',
        noDuplicate: true,
        isCode: true,
      });
    }
  }, [mergeRequest]);

  useEffect(() => {
    if (!mergeRequestId) {
      notify.error({
        id: 'invalid_merge_request_id_format',
        message: 'invalid_merge_request_id_format',
        isCode: true,
        noDuplicate: true,
      });
    }
  }, [mergeRequestId, notify]);

  const handleReject = () => {
    if (!mergeRequestId) return;

    mergeRequestActionMutation.mutate({
      data: { action: 'reject' },
    });
  };

  const handleApprove = () => {
    if (!mergeRequestId) return;

    const notifyId = notify.info({
      id: 'merge-request-started',
      message: t`Merge request approval has been started`,
    });
    setDismissNotifyId(notifyId);

    const userMappings =
      mergeRequest?.data?.legacyUsers.map(user => {
        let selectedRoleId = parseInt(
          selectedRoles[user.legacyUserId] || user.selectedRole?.id.toString() || '0'
        );
        let designerOrganizationIds = (
          selectedOrganizations[user.legacyUserId] ||
          user.selectedDesignerOrganizations?.map(org => org.id) ||
          []
        ).map(id => parseInt(id));
        if (user.type === 'client_user') {
          selectedRoleId = 5;
          if (['Scenario 7', 'Scenario 8'].indexOf(user.userMergeScenario) > -1) {
            selectedRoleId = 2;
          }
          designerOrganizationIds = user.availableDesignerOrganizations?.length
            ? [user.availableDesignerOrganizations[0].id]
            : [];
        } else if (designerOrganizationIds.includes(-1)) {
          designerOrganizationIds = user.availableDesignerOrganizations
            .map(o => o.id)
            .filter(id => id !== -1);
        } else {
          designerOrganizationIds = designerOrganizationIds.filter(id => id !== -1);
        }

        const mapping: {
          legacyUserId: string;
          selectedRoleId: number;
          designerOrganizationIds: number[];
          action: 'skip' | '';
        } = {
          legacyUserId: user.legacyUserId,
          selectedRoleId,
          action: user.showSkip && skipUsers[user.legacyUserId] ? 'skip' : '',
          designerOrganizationIds,
        };
        return mapping;
      }) || [];

    mergeRequestActionMutation.mutate({
      data: {
        mergeRequestId,
        action: 'approve',
        userMappings,
      },
    });
  };

  if (isLoading) {
    return (
      <LoadingWrapper>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </LoadingWrapper>
    );
  }

  if (error || mergeRequest?.data?.notFound) {
    return null;
  }

  return (
    <Modal
      open
      id="merge-request"
      hideDontShowAgain
      hideCloseIcon
      title={t`Account merge request`}
      actions={[
        {
          id: 'cancel',
          label: t`Reject`,
          variant: 'secondary',
          onClick: () => handleReject(),
          disabled: mergeRequestActionMutation.isPending,
          sx: { minWidth: 210 },
        },
        {
          id: 'apply',
          label: t`Merge`,
          variant: 'primary',
          onClick: () => handleApprove(),
          disabled: mergeRequestActionMutation.isPending,
          sx: { minWidth: 210 },
        },
      ]}
      modalSx={{
        window: {
          width: '100vw',
          maxWidth: '1114px',
        },
        windowBody: {
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
        },
      }}
    >
      <Container>
        <Body>
          <TextContainer>
            <Description>
              <Text weight="semiBold">{mergeRequest.data.designerOrganization.name}</Text>{' '}
              <Text color="secondary">
                <Trans>wants to merge the legacy account</Trans>
              </Text>{' '}
              <Text weight="semiBold">{mergeRequest.data.legacyOrganization.name}</Text>{' '}
              <Text color="secondary">
                <Trans>
                  into your supplier portal. Please review and assign roles to the previous
                  account&apos;s users
                </Trans>
              </Text>
            </Description>
            <Legend>
              <div>
                <Text variant="bodySm" color="secondary" weight="bold">
                  <Trans>Supplier admin</Trans>
                </Text>{' '}
                <Text color="secondary">
                  <Trans>can manage users and account details. Access to all clients.</Trans>
                </Text>
              </div>
              <div>
                <Text variant="bodySm" color="secondary" weight="bold">
                  <Trans>Supplier user</Trans>
                </Text>{' '}
                <Text color="secondary">
                  <Trans>
                    can perform supplier actions on behalf of either all or just assigned clients.
                  </Trans>
                </Text>
              </div>
              <div>
                <Text variant="bodySm" color="secondary" weight="bold">
                  <Trans>Client user</Trans>
                </Text>{' '}
                <Text color="secondary">
                  <Trans>can perform supplier actions on behalf their own client account.</Trans>
                </Text>
              </div>
              <div>
                <Text variant="bodySm" color="secondary" weight="bold">
                  <Trans>Inactive</Trans>
                </Text>{' '}
                <Text color="secondary">
                  <Trans>a former user account that should no longer have access.</Trans>
                </Text>
              </div>
            </Legend>
          </TextContainer>
          <UserList>
            <UserListHeader>
              <Text variant="headlineXs" weight="semiBold">
                <Trans>Users</Trans>
              </Text>
              <Text variant="headlineXs" weight="semiBold">
                <Trans>Role</Trans>
              </Text>
              <Text variant="headlineXs" weight="semiBold">
                <Trans>Companies</Trans>
              </Text>
            </UserListHeader>
            {mergeRequest.data.legacyUsers.map(user => {
              let companyValue = selectedOrganizations[user.legacyUserId] || [];
              const companyOptions = user.availableDesignerOrganizations.map(org => ({
                id: org.id.toString(),
                label: org.label,
              }));
              let roleValue =
                selectedRoles[user.legacyUserId] || user.selectedRole?.id.toString() || null;
              const roleOptions =
                user.availableRoles?.map(role => ({
                  id: role.id.toString(),
                  label: role.label,
                })) || [];
              if (user.type === 'client_user') {
                roleValue = '3';
                roleOptions.push({ id: '3', label: 'Client User' });
                companyValue = user.availableDesignerOrganizations?.length
                  ? [user.availableDesignerOrganizations[0].id.toString()]
                  : [];
              }
              if (user.type === 'supplier_admin') {
                // For supplier admin, use the selected role and organizations from backend
                roleValue = user.selectedRole?.id.toString() || null;
                companyValue =
                  user.selectedDesignerOrganizations?.map(org => org.id.toString()) || [];
              }
              if (user.type === 'inactive') {
                roleValue = '4';
                roleOptions.push({ id: '4', label: 'Inactive' });
              }
              const companySelect = (
                <Select
                  options={companyOptions}
                  value={companyValue}
                  onChange={e => {
                    const newValue = e.target.value as string[];
                    const previousValue = selectedOrganizations[user.legacyUserId] || [];
                    // deselect all brands if all brands option is selected
                    if (!previousValue.includes('-1') && newValue.includes('-1')) {
                      setSelectedOrganizations(prev => ({
                        ...prev,
                        [user.legacyUserId]: ['-1'],
                      }));
                      return;
                    }
                    // initiating designer company should always be selected
                    if (
                      user.type === 'supplier_user' &&
                      !newValue.includes(mergeRequest.data.designerOrganization.id.toString())
                    ) {
                      setSelectedOrganizations(prev => ({
                        ...prev,
                        [user.legacyUserId]: [
                          ...newValue.filter(id => id !== '-1'),
                          mergeRequest.data.designerOrganization.id.toString(),
                        ],
                      }));
                      return;
                    }
                    setSelectedOrganizations(prev => ({
                      ...prev,
                      [user.legacyUserId]: newValue.filter(id => id !== '-1'),
                    }));
                  }}
                  disabled={
                    !user.isEditable ||
                    user.availableDesignerOrganizations.length === 0 ||
                    roleValue === '2'
                  }
                  multiple
                  placeholder={
                    user.availableDesignerOrganizations.length === 0
                      ? t`No companies available`
                      : undefined
                  }
                />
              );
              return (
                <UserRow key={user.legacyUserId}>
                  <UserCell>
                    <TwoLineCell avatar first={user.name} second={user.email} big />
                  </UserCell>
                  {!user.showDropdowns ? (
                    <UserCellDouble>
                      <Text variant="labelMd" weight="semiBold" color="placeholder">
                        {user.displayNote}
                      </Text>
                    </UserCellDouble>
                  ) : (
                    <UserCellDouble>
                      <UserCellDoubleRow>
                        <UserCell>
                          <Select
                            options={roleOptions}
                            value={roleValue}
                            onChange={e => {
                              const newRoleId = e.target.value as string;
                              const previousRoleId = selectedRoles[user.legacyUserId] || null;
                              setSelectedRoles(prev => ({
                                ...prev,
                                [user.legacyUserId]: newRoleId,
                              }));
                              // set companies to all brands for admins
                              if (newRoleId === '2') {
                                setSelectedOrganizations(prev => ({
                                  ...prev,
                                  [user.legacyUserId]: ['-1'],
                                }));
                              }
                              // revert to original when changing to user
                              if (newRoleId === '5' && previousRoleId !== '5') {
                                setSelectedOrganizations(prev => ({
                                  ...prev,
                                  [user.legacyUserId]:
                                    user.selectedDesignerOrganizations?.map(org =>
                                      org.id.toString()
                                    ) || [],
                                }));
                              }
                            }}
                            disabled={!user.isEditable}
                          />
                        </UserCell>
                        {user.showSkip ? (
                          <UserCellWithCheckbox>
                            <SelectWithCheckbox>{companySelect}</SelectWithCheckbox>
                            <Checkbox
                              checked={skipUsers[user.legacyUserId] ?? true}
                              onChange={(e, checked) =>
                                setSkipUsers(prev => ({
                                  ...prev,
                                  [user.legacyUserId]: checked,
                                }))
                              }
                              label={<Trans>Skip</Trans>}
                            />
                          </UserCellWithCheckbox>
                        ) : (
                          <UserCell>{companySelect}</UserCell>
                        )}
                      </UserCellDoubleRow>
                      {user.displayNote ? <Text variant="bodyXs">{user.displayNote}</Text> : null}
                    </UserCellDouble>
                  )}
                </UserRow>
              );
            })}
          </UserList>
        </Body>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background-color: ${p => p.theme.palette.bg.primary};
  border-radius: 16px;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: ${p => p.theme.s.md};
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const UserListHeader = styled.div`
  display: flex;
  gap: 40px;
  padding-left: 0;
  > * {
    width: 320px;
  }
`;

const UserRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  min-height: 78px;
`;

const UserCell = styled.div`
  width: 320px;
  .MuiFormControl-root {
    margin: 0;
  }
`;

const UserCellWithCheckbox = styled.div`
  width: 320px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SelectWithCheckbox = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserCellDouble = styled.div`
  width: 680px;
  display: flex;
  flex-direction: column;
`;

const UserCellDoubleRow = styled.div`
  width: 680px;
  display: flex;
  flex-direction: row;
  gap: 40px;
`;

const TextContainer = styled.div`
  width: 700px;
`;

const Description = styled.div`
  margin-bottom: ${p => p.theme.s.xl};
`;

const Legend = styled.div`
  width: 100%;
  background-color: ${p => p.theme.palette.utility.grey[50]};
  border: 1px solid ${p => p.theme.palette.border.primarySubtle};
  margin-bottom: ${p => p.theme.s.xl};
  padding: ${p => p.theme.s.md};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${p => p.theme.s.xl};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${p => p.theme.s.xl};
`;
